import {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useEffectEvent,
  useState,
} from 'react';

const STEP_DURATION = 30;

function noop() {
  // NOOP
}

/** A run of text with optional inline styling (bold / color). */
export type TextSegmentT = {
  bold?: boolean;
  color?: string;
  text: string;
};

type PropsT = {
  children: TextSegmentT[] | string;
  onGrowth?: () => void;
  pre?: boolean;
  speed?: number;
};

function getFullText(children: TextSegmentT[] | string): string {
  return typeof children === 'string'
    ? children
    : children.map((segment) => segment.text).join('');
}

/** Splits the currently revealed prefix back across the given segments, so
 *  each segment's own bold/color styling is preserved while it types out. */
function renderSegments(visible: string, segments: TextSegmentT[]): ReactNode {
  const nodes: ReactNode[] = [];
  let consumed = 0;
  for (let i = 0; i < segments.length && consumed < visible.length; ++i) {
    const segment = segments[i];
    if (!segment) throw Error('Missing segment');
    const chunk = segment.text.slice(0, visible.length - consumed);
    if (chunk) {
      nodes.push(
        <span
          key={i}
          style={{
            color: segment.color,
            fontWeight: segment.bold ? 700 : undefined,
          }}
        >
          {chunk}
        </span>,
      );
    }
    consumed += chunk.length;
  }
  return nodes;
}

/**
 * Renders given text, revealing it symbol-by-symbol, similarly to a typewriter.
 * `children` may also be an array of `TextSegmentT` to style specific runs of
 * the text (bold / color) while preserving the same reveal animation.
 */
const TypewriterText: FunctionComponent<PropsT> = ({
  children,
  onGrowth,
  pre,
  speed = 1,
}) => {
  if (speed <= 0) throw Error('`speed` must be a positive number');

  const fullText = getFullText(children);

  const [visible, setVisible] = useState(fullText[0] ?? '');

  // Keeps `visible` a prefix of `fullText` when the text changes mid-reveal:
  // the revealed part becomes the new text's prefix of the same length.
  if (!fullText.startsWith(visible)) {
    setVisible(fullText.slice(0, visible.length));
  }

  const emitGrowth = useEffectEvent(onGrowth ?? noop);

  useEffect(() => {
    let timerId: NodeJS.Timeout;
    const addSymbol = () => {
      setVisible((prev) => {
        if (prev.length + 1 < fullText.length) {
          timerId = setTimeout(addSymbol, STEP_DURATION / speed);
        }
        return `${prev}${fullText[prev.length] ?? ''}`;
      });
      emitGrowth();
    };
    timerId = setTimeout(addSymbol, STEP_DURATION / speed);
    return () => {
      clearTimeout(timerId);
    };
  }, [fullText, speed]);

  const content = typeof children === 'string'
    ? visible
    : renderSegments(visible, children);

  return pre ? <pre>{content}</pre> : <div>{content}</div>;
};

export default TypewriterText;
